// src/utils/qrcode/service.ts
import QRCodeStyling from 'qr-code-styling';
import { QRStyleConfig, QRDownloadOptions } from './types';

export class QRCodeService {
    private qr: any;
    private _element: HTMLElement | null = null;

    constructor(config: QRStyleConfig) {
        // Create a proper configuration object that won't cause runtime errors
        const qrConfig: any = {
            width: config.width || 300,
            height: config.height || 300,
            data: config.value, // Use value from interface but pass as data to library
            margin: config.margin || 10,
            dotsOptions: {
                color: config.dotsOptions?.color || '#000000',
                type: config.dotsOptions?.type || 'square'
            },
            backgroundOptions: {
                color: config.backgroundOptions?.color || '#FFFFFF'
            }
        };

        // Only add optional properties if they exist
        if (config.dotsOptions?.backgroundColor) {
            qrConfig.dotsOptions.backgroundColor = config.dotsOptions.backgroundColor;
        }

        if (config.cornersSquareOptions) {
            qrConfig.cornersSquareOptions = {};
            if (config.cornersSquareOptions.color) qrConfig.cornersSquareOptions.color = config.cornersSquareOptions.color;
            if (config.cornersSquareOptions.type) qrConfig.cornersSquareOptions.type = config.cornersSquareOptions.type;
            if (config.cornersSquareOptions.backgroundColor) qrConfig.cornersSquareOptions.backgroundColor = config.cornersSquareOptions.backgroundColor;
        }

        if (config.cornersDotOptions) {
            qrConfig.cornersDotOptions = {};
            if (config.cornersDotOptions.color) qrConfig.cornersDotOptions.color = config.cornersDotOptions.color;
            if (config.cornersDotOptions.type) qrConfig.cornersDotOptions.type = config.cornersDotOptions.type;
            if (config.cornersDotOptions.backgroundColor) qrConfig.cornersDotOptions.backgroundColor = config.cornersDotOptions.backgroundColor;
        }

        // Carefully handle image - only add if src is provided
        if (config.image) {
            // Create a new image element to preload and check the image
            const img = new Image();
            img.crossOrigin = "anonymous"; // This is crucial for external images
            
            // Set up load and error handlers
            img.onload = () => {
                console.log("Image loaded successfully:", config.image);
                // Now that we know the image is valid, create the QR code
                qrConfig.image = config.image;
                qrConfig.imageOptions = {
                    hideBackgroundDots: config.imageOptions?.hideBackgroundDots ?? true,
                    imageSize: config.imageOptions?.imageSize ?? 0.4,
                    margin: config.imageOptions?.margin ?? 5,
                    crossOrigin: "anonymous"
                };
                
                try {
                    this.qr = new QRCodeStyling(qrConfig);
                    // If we successfully created a new QR code with the image, append it to the same element
                    if (this._element) {
                        this._element.innerHTML = '';
                        this.qr.append(this._element);
                    }
                } catch (error) {
                    console.error('Error creating QR code with image:', error);
                    delete qrConfig.image;
                    delete qrConfig.imageOptions;
                    this.qr = new QRCodeStyling(qrConfig);
                    if (this._element) {
                        this._element.innerHTML = '';
                        this.qr.append(this._element);
                    }
                }
            };
            
            img.onerror = () => {
                console.error("Failed to load image:", config.image);
                // Create QR code without the image
                delete qrConfig.image;
                delete qrConfig.imageOptions;
                this.qr = new QRCodeStyling(qrConfig);
            };
            
            // Start loading the image
            img.src = config.image;
            
            // Create a basic QR code immediately, will be updated when image loads
            delete qrConfig.image;
            delete qrConfig.imageOptions; // Remove image for initial creation
            this.qr = new QRCodeStyling(qrConfig);
        } else {
            // No image, create QR code directly
            this.qr = new QRCodeStyling(qrConfig);
        }
    }

    async downloadQR(options: QRDownloadOptions): Promise<void> {
        if (!this.qr) return;
        
        const extension = options.extension || 'svg';
        const name = options.name || `qrcode-${Date.now()}`;
        const quality = options.quality || 1.0;
        
        await this.qr.download({
            name,
            extension,
            quality
        });
    }

    async updateConfig(config: Partial<QRStyleConfig>): Promise<void> {
        if (!this.qr) return;
        
        const updateConfig: any = {};
        
        // Only add properties that are defined
        if (config.width) updateConfig.width = config.width;
        if (config.height) updateConfig.height = config.height;
        if (config.value) updateConfig.data = config.value; // Convert value to data
        if (config.margin !== undefined) updateConfig.margin = config.margin;
        
        if (config.dotsOptions) {
            updateConfig.dotsOptions = {};
            if (config.dotsOptions.color) updateConfig.dotsOptions.color = config.dotsOptions.color;
            if (config.dotsOptions.type) updateConfig.dotsOptions.type = config.dotsOptions.type;
            if (config.dotsOptions.backgroundColor) updateConfig.dotsOptions.backgroundColor = config.dotsOptions.backgroundColor;
        }
        
        if (config.cornersSquareOptions) {
            updateConfig.cornersSquareOptions = {};
            if (config.cornersSquareOptions.color) updateConfig.cornersSquareOptions.color = config.cornersSquareOptions.color;
            if (config.cornersSquareOptions.type) updateConfig.cornersSquareOptions.type = config.cornersSquareOptions.type;
            if (config.cornersSquareOptions.backgroundColor) updateConfig.cornersSquareOptions.backgroundColor = config.cornersSquareOptions.backgroundColor;
        }
        
        if (config.cornersDotOptions) {
            updateConfig.cornersDotOptions = {};
            if (config.cornersDotOptions.color) updateConfig.cornersDotOptions.color = config.cornersDotOptions.color;
            if (config.cornersDotOptions.type) updateConfig.cornersDotOptions.type = config.cornersDotOptions.type;
            if (config.cornersDotOptions.backgroundColor) updateConfig.cornersDotOptions.backgroundColor = config.cornersDotOptions.backgroundColor;
        }
        
        if (config.backgroundOptions) {
            updateConfig.backgroundOptions = {
                color: config.backgroundOptions.color
            };
        }
        
        // Handle image updates separately to ensure proper loading
        if (config.image) {
            // Create a new image element to preload and check the image
            const img = new Image();
            img.crossOrigin = "anonymous";
            
            img.onload = () => {
                console.log("Image loaded successfully for update:", config.image);
                updateConfig.image = config.image;
                updateConfig.imageOptions = {
                    hideBackgroundDots: config.imageOptions?.hideBackgroundDots ?? true,
                    imageSize: config.imageOptions?.imageSize ?? 0.4,
                    margin: config.imageOptions?.margin ?? 5,
                    crossOrigin: "anonymous"
                };
                
                try {
                    this.qr.update(updateConfig);
                } catch (error) {
                    console.error('Error updating QR code with image:', error);
                    delete updateConfig.image;
                    delete updateConfig.imageOptions;
                    this.qr.update(updateConfig);
                }
            };
            
            img.onerror = () => {
                console.error("Failed to load image for update:", config.image);
                delete updateConfig.image;
                delete updateConfig.imageOptions;
                this.qr.update(updateConfig);
            };
            
            // Start loading the image
            img.src = config.image;
            
            // Update other properties immediately
            const imageConfig = { ...updateConfig };
            delete imageConfig.image;
            delete imageConfig.imageOptions;
            this.qr.update(imageConfig);
        } else {
            // No image or image was removed
            if (config.image === undefined) {
                // Don't touch the image property if it wasn't specified
            } else {
                // Explicitly remove the image if it was set to null/undefined
                updateConfig.image = null;
                updateConfig.imageOptions = null;
            }
            
            try {
                this.qr.update(updateConfig);
            } catch (error) {
                console.error('Error updating QR code:', error);
                delete updateConfig.image;
                delete updateConfig.imageOptions;
                this.qr.update(updateConfig);
            }
        }
    }

    appendToElement(element: HTMLElement): void {
        this._element = element;
        if (this.qr) {
            this.qr.append(element);
        }
    }

    getCanvas(): HTMLCanvasElement | null {
        return this.qr ? this.qr._canvas : null;
    }

    getSVG(): SVGElement | null {
        return this.qr ? this.qr._svg : null;
    }
}